import type { IList, IPerson } from '@dakedake/types'
import { modelName } from '@dakedake/types'
import { getBgm } from '@/services'
import { tmdbPic } from './tmdb'

const boldTagRegex = /\[b\](.*?)\[\/b\]/g
const allTagRegex = /\[.*?\]/g

/**
 * 格式化分类列表
 * @param list 分类列表
 * @returns array
 */

export function getList<T extends IList>(list: T[]) {
  const data: ({ sub?: T[] } & T)[] = []
  list.forEach(item => {
    if (+item.pid! === 0)
      data.push({ ...item, sub: [] })
  })
  data.forEach(item => {
    const arr = list.filter(s => +s.pid! === +item.id!)
    if (arr.length)
      item.sub = arr
  })
  return data
}

/**
 * 判断是否是数字
 * @param val 参数
 * @returns boolean
 */

export function isRealNum(val: any) {
  // isNaN()函数 把空串 空格 以及NUll 按照0来处理 所以先去除，
  if (val === '' || val == null)
    return false

  if (!Number.isNaN(val) && typeof val === 'number')
    return true

  else
    return false
}

/**
 * 把id转成string
 * @param data 需要转换的数据
 * @returns 返回id转成string
 */
export function idToStr(data: any[]) {
  data?.forEach(item => {
    if (isRealNum(item.id))
      item.id = String(item.id)

    if (isRealNum(item.pid))
      item.pid = String(item.pid)

    if (isRealNum(item.cid))
      item.cid = String(item.cid)

    if (isRealNum(item.mid))
      item.mid = String(item.mid)

    if (isRealNum(item.sid))
      item.sid = String(item.sid)
  })
  return data
}

export const sidObj = [
  { label: '动漫', value: modelName.SUBJECT },
  { label: '新闻', value: modelName.NEWS },
  { label: '人物', value: modelName.PERSON },
  { label: '剧集', value: modelName.EPISODE },
  { label: '角色', value: modelName.CHARACTER },
  { label: '剧集评分', value: modelName.SUBJECT_INTEREST },
  { label: '标签', value: modelName.TAG },
  { label: '动态', value: modelName.PIN },
  { label: '转发', value: modelName.FORWARD },
  { label: '评论', value: modelName.COMMENT },
  { label: '回复', value: modelName.REPLY },
  { label: '动态', value: modelName.FEED },
  { label: '话题', value: modelName.TOPIC },
  { label: '清单', value: modelName.LISTS },
  { label: '演员', value: modelName.ACOTR },
  { label: '用户', value: modelName.USER }
]
/**
 * 格式模型
 * @returns 返回模型名称
 */
export function sidEnum() {
  let obj = {}
  sidObj.forEach(item => {
    obj = {
      ...obj,
      [item.value!]: {
        text: item.label
      }
    }
  })
  return obj
}

export const department = [
  { value: 'Art', label: '艺术' },
  { value: 'Visual Effects', label: '视效' },
  { value: 'Directing', label: '导演' },
  { value: 'Writing', label: '编辑' },
  { value: 'Camera', label: '摄像' },
  { value: 'Editing', label: '剪辑' },
  { value: 'Production', label: '制片' },
  { value: 'Sound', label: '录音' },
  { value: 'Costume & Make-Up', label: '服装及制作' },
  { value: 'Crew', label: '机组人员' },
  { value: 'Lighting', label: '灯光' },
  { value: 'Acting', label: '演员' }
]

export const job = [
  { value: 'voice', label: '配音' },
  { value: 'Visual Effects', label: '视效' },
  { value: 'Directing', label: '导演' },
  { value: 'Writing', label: '编辑' },
  { value: 'Camera', label: '摄像' },
  { value: 'Editing', label: '剪辑' },
  { value: 'Production', label: '制片' },
  { value: 'Sound', label: '录音' },
  { value: 'Costume & Make-Up', label: '服装及制作' },
  { value: 'Crew', label: '机组人员' },
  { value: 'Lighting', label: '灯光' },
  { value: 'Acting', label: '演员' }
]

export async function personFormat(casts: any[]) {
  const starList: IPerson[] = []
  for await (const item of casts) {
    const res = await getBgm({ path: `persons/${item.person?.id || item.id}` })
    const { infobox, summary, career, id, nameCN: name, name: title, images } = res || {}
    const boxObj = infobox.reduce((pre: any, cur: { key: any, values: any[] }) => ({ ...pre, [cur.key]: cur.values?.[0]?.v || '' }), {})
    const alias = infobox.find((item: any) => item.key === '别名')?.values?.filter((item: any) => !!item.v)?.map((item: any) => item.v) || []
    const birthday = boxObj['生日'] || ''
    const blood_type = boxObj['血型'] || ''
    const height = boxObj['身高'] || ''
    const weight = boxObj['体重'] || ''
    const birthplace = boxObj['出生地'] || ''
    const workplace = boxObj['事务所'] || ''
    const blog = boxObj['个人博客'] || ''
    const twitter = boxObj.Twitter || ''
    const reference = boxObj['引用来源'] || ''
    const website = boxObj['官网'] || ''
    const official_website = boxObj['官方网站'] || ''
    const hp = boxObj.HP || ''
    const gender = boxObj['性别'] === '男' ? 2 : boxObj['性别'] === '女' ? 1 : 0
    const record_company = boxObj['唱片公司'] || ''
    const founded = boxObj['成立时间'] || ''
    const chairman = boxObj['代表取締役社長'] || ''
    const chairman_title = boxObj['取締役会長'] || ''
    const chairman_sub = boxObj['取締役副社長'] || ''
    const chairman_sub_title = boxObj['取締役副社長職稱'] || ''
    const executive = boxObj['執行役員'] || ''
    const executive_title = boxObj['執行役員職稱'] || ''
    const employee = boxObj['員工數'] || ''
    const link = boxObj['链接'] || ''
    const chairman_board = boxObj['代表取缔役社长'] || ''
    const slogan = boxObj['公司口号'] || ''
    const business_scope = boxObj['经营范围'] || ''
    const headquarter = boxObj['总部地点'] || ''
    const parent_company = boxObj['母公司'] || ''
    const info = [
      { key: '血型', value: blood_type },
      { key: '身高', value: height },
      { key: '体重', value: weight },
      { key: '出生地', value: birthplace },
      { key: '事务所', value: workplace },
      { key: '个人博客', value: blog },
      { key: 'Twitter', value: twitter },
      { key: '引用来源', value: reference },
      { key: '官网', value: website || official_website || hp },
      { key: '成立时间', value: founded },
      { key: '代表取締役社長', value: chairman },
      { key: '取締役会長', value: chairman_title },
      { key: '取締役副社長', value: chairman_sub },
      { key: '取締役副社長職稱', value: chairman_sub_title },
      { key: '執行役員', value: executive },
      { key: '執行役員職稱', value: executive_title },
      { key: '員工數', value: employee },
      { key: '链接', value: link },
      { key: '代表取缔役社长', value: chairman_board },
      { key: '公司口号', value: slogan },
      { key: '经营范围', value: business_scope },
      { key: '总部地点', value: headquarter },
      { key: '母公司', value: parent_company },
      { key: '唱片公司', value: record_company }].filter(item => item.value)
    const list = {
      alias,
      content: summary?.replace(boldTagRegex, '$1\n').replace(allTagRegex, '').trim() || '',
      name: name || title,
      title: title || name,
      gender,
      career: career.join(','),
      birthday,
      source: id,
      area: birthplace,
      appear_eps: item?.appearEps || '',
      info,
      cover: images?.large ? await tmdbPic({ url: images.large, sid: modelName.PERSON, type: 'posters', apiType: 'person', typeId: id, noTmdb: true }) : 0
    }
    starList.push(list)
  }
  return starList
}

export const getTmdbId = (id: string) => Number(id?.split('/')[1] || id || 0)
export const isTmdbTvOrMovie = (id: string) => id.includes('movie') ? 'movie' : 'tv'
